import useCallbackRef from 'hooks/useCallbackRef';
import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import { SideDrawerRef } from '../../SideDrawer/SideDrawer.component';
import {
  Question,
  QuestionManagingDrawerCommonProps,
} from 'router/subrouters/DataRoom/models/QAndA';
import {
  generateActionKey,
  MANAGE_ACTIONS_KEYS,
  APPROVE_ROLES_ALLOWED_TO_ACTION,
  ManageActionKeys,
} from '../utils';
import PreviewQuestionDrawerComponent from '../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';
import { WriteAnswerDrawer, ManageAnswer, ManageQuestion } from '../components';
import { useTranslation } from 'react-i18next';
import { isQASellExpert } from 'router/subrouters/DataRoom/utils';

const MANAGE_CONTENT: Record<
  ManageActionKeys,
  ForwardRefExoticComponent<
    QuestionManagingDrawerCommonProps & RefAttributes<SideDrawerRef>
  >
> = {
  needsAns: WriteAnswerDrawer,
  needsApproval: ManageAnswer,
  published: PreviewQuestionDrawerComponent,
  inReview: ManageQuestion,
};

const useManageQuestion = (question: Question) => {
  const [drawer, ref] = useCallbackRef<SideDrawerRef>(null);
  const { t } = useTranslation();

  const {
    qaRole,
    dataRoom: {
      currentMember: { userId },
    },
  } = useContext(DataRoomContext);
  const { updateQuestion, refetch } = useContext(QAndAContext);
  const key = generateActionKey(question.status);

  const YourDrawerType = useMemo(() => MANAGE_CONTENT[key], [key]);

  const manageButtonText = useMemo(() => {
    if (key === MANAGE_ACTIONS_KEYS.NEEDS_ANSWER) return 'answer';
    if (
      key === MANAGE_ACTIONS_KEYS.NEEDS_APPROVAL ||
      key === MANAGE_ACTIONS_KEYS.IN_REVIEW
    )
      return 'review';
    if (key === MANAGE_ACTIONS_KEYS.PUBLISHED) return 'preview';

    return 'manage';
  }, [key]);

  const notAllowed =
    !APPROVE_ROLES_ALLOWED_TO_ACTION[key]?.includes(qaRole) || !YourDrawerType;

  const notIcludedExpert =
    isQASellExpert(qaRole) && !question.assignedUserIds.includes(userId);

  const cantManageQuestion = notAllowed || notIcludedExpert;

  const YourDrawer = useMemo(
    () =>
      !cantManageQuestion ? (
        <YourDrawerType
          ref={ref}
          question={question}
          onQuestionsUpdate={refetch}
          updateQuestion={updateQuestion}
        />
      ) : null,
    [
      YourDrawerType,
      cantManageQuestion,
      question,
      ref,
      refetch,
      updateQuestion,
    ],
  );

  const onManageClick = useCallback(() => drawer.setOpen(true), [drawer]);

  return {
    YourDrawer,
    cantManageQuestion,
    manageButtonText: t(manageButtonText),
    onManageClick,
  };
};

export default useManageQuestion;
